import React from 'react';
import { useTranslation } from 'react-i18next';

import Markdown from '../common/Markdown';
import CoachOptions, { OptionKindBadge } from '../WorkoutDebrief/CoachOptions';

export default function CoachAnalysisRenderer({ analysis }) {
  const { t } = useTranslation();

  if (!analysis) return null;

  // Coach v2 proposal (missed session, event…): read-only rendering.
  if (analysis?.version === 2 && (analysis.coach_read || Array.isArray(analysis.options))) {
    return <CoachOptions analysis={analysis} canEdit={false} showReadOnlyNotice={false} />;
  }

  // History entry of an applied coach v2 option: { analysis: coach_read, option, source }.
  if (analysis?.option && typeof analysis.option === 'object') {
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {analysis.option.kind ? <OptionKindBadge kind={analysis.option.kind} /> : null}
          <span className="text-sm font-black text-gray-900">{analysis.option.label}</span>
        </div>
        {typeof analysis.analysis === 'string' && analysis.analysis ? (
          <Markdown className="text-sm text-gray-700">{analysis.analysis}</Markdown>
        ) : null}
        {analysis.option.rationale ? <div className="text-xs text-gray-500">{analysis.option.rationale}</div> : null}
      </div>
    );
  }

  if (analysis?.closure_type && analysis?.summary) {
    const whatWorked = Array.isArray(analysis.summary.what_worked) ? analysis.summary.what_worked : [];
    const attentionPoints = Array.isArray(analysis.summary.attention_points) ? analysis.summary.attention_points : [];

    return (
      <div className="space-y-4">
        {analysis.headline ? (
          <div className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-indigo-700">
            {analysis.headline}
          </div>
        ) : null}

        {analysis.athlete_comment_summary ? (
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-400">
              {t('plan.end.analysis.comment_summary')}
            </div>
            <div className="text-sm text-gray-700">{analysis.athlete_comment_summary}</div>
          </div>
        ) : null}

        {analysis.overall_assessment ? (
          <Markdown className="text-sm leading-relaxed text-gray-700">
            {analysis.overall_assessment}
          </Markdown>
        ) : null}

        {whatWorked.length > 0 ? (
          <div>
            <div className="mb-1 text-sm font-bold text-green-700">{t('plan.end.analysis.what_worked')}</div>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
              {whatWorked.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {attentionPoints.length > 0 ? (
          <div>
            <div className="mb-1 text-sm font-bold text-amber-600">{t('plan.end.analysis.attention_points')}</div>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
              {attentionPoints.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {analysis.summary.next_step ? (
          <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
            <div className="mb-1 text-xs font-bold uppercase tracking-wide text-indigo-500">
              {t('plan.end.analysis.next_step')}
            </div>
            <div className="text-sm font-medium text-indigo-900">{analysis.summary.next_step}</div>
          </div>
        ) : null}
      </div>
    );
  }

  // 1. Try to find the "summary" block (Verdict, Good job, Attention, Focus)
  // It can be at the root (newer format) or nested in 'analysis' (older format)
  let summary = analysis?.summary;
  if (!summary && analysis?.analysis?.summary) {
    summary = analysis.analysis.summary;
  }

  if (summary) {
    return (
      <div className="space-y-4">
        {summary.verdict && (
          <div className="flex items-center gap-2">
            <span className="font-bold text-indigo-900">Verdict:</span>
            <span
              className={`rounded px-2 py-0.5 text-sm font-bold ${
                summary.verdict.toLowerCase().includes('ok') || summary.verdict.toLowerCase().includes('bien')
                  ? 'bg-green-100 text-green-800'
                  : summary.verdict.toLowerCase().includes('risque') || summary.verdict.toLowerCase().includes('blessure')
                    ? 'bg-red-100 text-red-800'
                    : 'bg-blue-100 text-blue-800'
              }`}
            >
              {summary.verdict}
            </span>
          </div>
        )}

        {summary.one_key_focus_next && (
          <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-3">
            <span className="mb-1 block font-bold text-indigo-900">Focus:</span>
            <span className="text-sm text-indigo-800">{summary.one_key_focus_next}</span>
          </div>
        )}

        {summary.good_job && summary.good_job.length > 0 && (
          <div>
            <div className="mb-1 text-sm font-bold text-green-700">Good Job:</div>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
              {summary.good_job.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {summary.attention_points && summary.attention_points.length > 0 && (
          <div>
            <div className="mb-1 text-sm font-bold text-amber-600">Attention:</div>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
              {summary.attention_points.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  if (analysis.audit_status) {
    return (
      <div className="space-y-2">
        <div className="font-bold text-indigo-900">Audit: {analysis.audit_status}</div>
        {analysis.audit_notes && analysis.audit_notes.length > 0 && (
          <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
            {analysis.audit_notes.map((note, index) => (
              <li key={index}>{note}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (analysis.status && analysis.analysis) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="font-bold text-gray-700">Status:</span>
          <span
            className={`rounded px-2 py-0.5 text-xs font-bold uppercase ${
              analysis.status === 'feasible'
                ? 'bg-green-100 text-green-800'
                : analysis.status === 'risky'
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-gray-100 text-gray-800'
            }`}
          >
            {analysis.status}
          </span>
        </div>

        <Markdown className="text-sm leading-relaxed text-gray-700">{analysis.analysis}</Markdown>

        {analysis.suggestions && analysis.suggestions.length > 0 && (
          <div className="mt-3 border-t border-gray-100 pt-3">
            <div className="mb-2 text-xs font-bold uppercase text-gray-500">{t('modals.coach_suggestions')}</div>
            <div className="space-y-2">
              {analysis.suggestions.map((suggestion, index) => (
                <div key={index} className="rounded border border-gray-100 bg-gray-50 p-2">
                  <div className="text-xs font-bold text-indigo-900">{suggestion.label || suggestion.id}</div>
                  {suggestion.description ? (
                    <div className="mt-1 text-xs text-gray-600">{suggestion.description}</div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (typeof analysis === 'string') {
    return <Markdown className="text-sm text-gray-700">{analysis}</Markdown>;
  }

  if (analysis.analysis && typeof analysis.analysis === 'string') {
    return <Markdown className="text-sm text-gray-700">{analysis.analysis}</Markdown>;
  }

  return (
    <pre className="overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-500">
      {JSON.stringify(analysis, null, 2)}
    </pre>
  );
}
